import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Grid,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { styled } from "@mui/system";
import { PriceModel } from "./data/PriceModel";
import { updatePriceValues } from "./data/dataUtils";
import GrowthRow from "./GrowthRow";
import BaseValueRow from "./BaseValueRow";
import UnitsRow from "./UnitsRow";
import EnergyTypeRow from "./EnergyTypeRow";
import RegionRow from "./RegionRow";
import { useParams } from "react-router-dom";
import { _addPriceList, _getPriceList } from "middlewares/Settings/prices";
import Spinner from "components/Spinner";
import { Currency, TitleDefinitionConstants, UserRoles } from "constants/constant";
import { useAppState } from "context/appState.context";
import SnackBar from "components/SnackBar";
import { AlertsProps } from "components/Alerts/data/Alerts.type";

const StyledTableContainer = styled(TableContainer)({
  maxWidth: "100%",
  overflowX: "auto",
});

const StyledTableCell = styled(TableCell)({
  minWidth: 105,
  padding: "9px",
});

/**
 * PricingTable component that displays a table of pricing data and allows for editing and saving.
 *
 * @component
 * @example
 * return (
 *   <PricingTable />
 * )
 */
const PricingTable: React.FC = () => {
  const { LocationId } = useParams();
  const { userRole, currency } = useAppState();
  const isUserRole = userRole !== UserRoles.Viewers;

  const [priceData, setPriceData] = useState<PriceModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [currencyText, setCurrencyText] = useState("");

  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });

  /**
   * Fetches the price list from the backend and updates the state with the retrieved data.
   *
   * @async
   * @function getPriceList
   * @returns {Promise<void>}
   */
  const getPriceList = async () => {
    try {
      setLoading(true);
      const res = await _getPriceList(Number(LocationId));
      if (res) {
        const updatedData = res.map((item: PriceModel, index: number) => ({
          ...item,
          uniqueId: `${item.energyType ?? ""}-${index}`,
          priceValue: item.priceValue,
        }));
        setPriceData(updatedData);
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPriceList();
  }, [LocationId]);

  useEffect(() => {
    if (currency) {
      const _currency = Currency.currency.find((c) => c.value === currency);
      setCurrencyText(_currency ? _currency.text  + ' ('  + _currency?.symbol + ')' : "");
      return;
    }

    setCurrencyText("");
  }, [currency]);

  /**
   * Calculates the range of years present in the price data.
   *
   * @returns {number[]} An array of years.
   */
  const years = useMemo(() => {
    const minYear = Math.min(...priceData.map((item) => item.baseYear));
    const maxYear = Math.max(
      ...priceData.flatMap((item) => item.priceValue.map((pv) => pv.year))
    );
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  }, [priceData]);

  /**
   * Handles closing the SnackBar component.
   * @param {AlertsProps} state - The new state for the SnackBar.
   */
  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  /**
   * Handles changes to price data fields and updates the state accordingly.
   *
   * @function handleDataChange
   * @param {string} id - The unique ID of the item being updated.
   * @param {keyof PriceModel} field - The field in the PriceModel being updated.
   * @param {string | number} value - The new value for the field.
   */
  const handleDataChange = (
    id: string,
    field: keyof PriceModel,
    value: string | number
  ) => {
    setPriceData((prevData) => {
      const updatedData = prevData.map((item) => {
        if (item.uniqueId === id) {
          const updatedItem = {
            ...item,
            [field]: field === "energyType" ? value : Number(value),
          };
          if (field === "growth" || field === "baseValue") {
            updatePriceValues(
              updatedItem.priceValue,
              updatedItem.baseValue,
              updatedItem.growth
            );
          }
          return updatedItem;
        }
        return item;
      });

      return updatedData;
    });
  };

  /**
   * Handles the save action, which adds the current price data to the backend.
   *
   * @async
   * @function handleSave
   * @returns {Promise<void>}
   */
  const handleSave = async () => {
    try {
      setLoading(true);
      let saveResult = true;

      for (const price of priceData) {
        let res = await _addPriceList([price]);
        if (!res) {
          saveResult = false;
        }
      }

      if (saveResult) {
        setLoading(false);
        const message = "Data Saved Successfully !";
        handleCloseSnackBar({
          show: true,
          severity: "success",
          message: message,
        });
        getPriceList();
      }

      setLoading(false);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formScale = 0.75;
  return (
    <Grid>
      {loading ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "100vh" }}
        >
          <Spinner size={80} data-testid="spinner" />
        </Grid>
      ) : (
        <Box
          sx={{
            transform: `scale(${formScale})`,
            transformOrigin: 'top left',
            width: `${100 / formScale}%`,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ mr: 5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold" }}
                data-testid="pricesLabel"
              >
                Prices
              </Typography>
            </Grid>
          </Grid>
          {priceData.length === 0 ? (
            <Grid item xs={11} style={{ textAlign: "center" }}>
              <Typography
                variant="h6"
                color="gray"
                style={{ fontWeight: "bold" }}
                sx={{ mt: 15, mb: 20, ml: 20 }}
              >
                {TitleDefinitionConstants.priceEmptyMessage}
              </Typography>
            </Grid>
          ) : (
            <>
              <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                {/* <!-- Left text --> */}
                <Grid item sx={{ mt: 4 }}>
                  <Typography variant="subtitle2">
                    Currency is: {currencyText}
                  </Typography>
                </Grid>
                {/* <!-- Right button --> */}
                <Grid item sx={{ ml: 2 }}>
                  {isUserRole && (
                    <Button type="submit"
                      variant="contained"
                      data-testid="btnSave"
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  )}
                </Grid>
              </Grid>

              <StyledTableContainer>
                <Table>
                  <TableBody>
                    <EnergyTypeRow priceData={priceData} />
                    <RegionRow priceData={priceData} />
                    <GrowthRow
                      priceData={priceData}
                      onChange={(id, value) =>
                        handleDataChange(id, "growth", value)
                      }
                      isUserRole={isUserRole}
                    />
                    <BaseValueRow
                      priceData={priceData}
                      onChange={(id, value) =>
                        handleDataChange(id, "baseValue", value)
                      }
                      isUserRole={isUserRole}
                    />
                    <UnitsRow priceData={priceData} />
                    {years.map((year) => (
                      <TableRow key={`price-${year}`}>
                        <StyledTableCell>{year}</StyledTableCell>
                        {priceData.map((item) => {
                          const priceValue = item.priceValue.find(
                            (pv) => pv.year === year
                          );
                          return (
                            <StyledTableCell key={`${year}-${item.uniqueId}`}>
                              {priceValue ? priceValue.value : 0}
                            </StyledTableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </>
          )}
          <SnackBar
            show={success.show}
            message={success.message}
            severity={success.severity}
            onclose={() =>
              handleCloseSnackBar({
                show: false,
                severity: "info",
                message: "",
              })
            }
          />
        </Box>
      )}
    </Grid>
  );
};

export default PricingTable;
